import React, { useEffect, useState } from "react";
import { 
  Layers, CheckCircle2, AlertTriangle, Globe, 
  ShieldCheck, Leaf, Activity, RefreshCw, Scale, Box, HelpCircle
} from "lucide-react";
import { getBatchAnalytics } from "../../../api/AnalyticsApi";

export default function BatchAnalytics() {
  const [analytics, setAnalytics] = useState({
    totalBatches: 0,
    completeBatches: 0,
    incompleteBatches: 0,
    euBatches: 0,
    usaBatches: 0,
    organicBatches: 0,
    conventionalBatches: 0,
    thirdPartySamplingYes: 0,
    thirdPartySamplingNo: 0,
    totalVolumeKgs: 0,
    averageBatchSizeKgs: 0,
    organicTotalKgs: 0,
    conventionalTotalKgs: 0,
    euTotalKgs: 0,
    usaTotalKgs: 0,
    euOrganicKgs: 0,
    euConventionalKgs: 0,
    usaOrganicKgs: 0,
    usaConventionalKgs: 0,
    
    euOrganicBatches: 0,
    euConventionalBatches: 0,
    usaOrganicBatches: 0,
    usaConventionalBatches: 0,

    productDistribution: []
  });
  const [loading, setLoading] = useState(true);
  
  // High-performance hover stream state engines
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);
  const [chartMode, setChartMode] = useState("batches"); // "batches" or "weight"

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await getBatchAnalytics();
      setAnalytics(res.data || analytics);
    } catch (err) {
      console.error("Batch telemetry aggregation stream failed", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoaderSkeleton />;

  const total = analytics.totalBatches || 0;
  
  const pctComplete = total ? Math.round((analytics.completeBatches / total) * 100) : 0;
  const pctOrganic = total ? Math.round((analytics.organicBatches / total) * 100) : 0;
  const pctEu = total ? Math.round((analytics.euBatches / total) * 100) : 0;
  const pctUsa = total ? Math.round((analytics.usaBatches / total) * 100) : 0;
  const pctSampling = total ? Math.round((analytics.thirdPartySamplingYes / total) * 100) : 0;

  const maxBatches = analytics.productDistribution?.length 
    ? Math.max(...analytics.productDistribution.map(p => p.batchCount)) 
    : 1;

  const maxWeight = analytics.productDistribution?.length 
    ? Math.max(...analytics.productDistribution.map(p => p.totalWeightKgs)) 
    : 1;

  return (
    <div className="batch-analytics-engine master-compact-density animate-fade-in">
      
      {/* CONTROL & ACTION SECTION BAR */}
      <div className="d-flex justify-content-between align-items-center mb-2 px-1">
        <div className="d-flex align-items-center gap-1.5">
          <Activity size={12} className="text-primary animate-pulse" />
          <span className="fw-bold tracking-wider text-slate-800 text-uppercase generic-header-title">
            Real-Time Batch Analytics Layer
          </span>
        </div>
        <button onClick={loadAnalytics} className="btn-refresh-stream" title="Refresh Streams">
          <RefreshCw size={11} />
        </button>
      </div>

      {/* BLOCK 1: OPERATIONAL COUNTER PERFORMANCE ROW */}
      <div className="row g-2 mb-2">
        <KPIBlock 
          title="Total Batches" 
          value={total} 
          icon={<Layers size={14} />} 
          color="#0d6efd" 
        />
        <KPIBlock 
          title="Complete Batches" 
          value={analytics.completeBatches} 
          subtitle={`${pctComplete}% Done`} 
          icon={<CheckCircle2 size={14} />} 
          color="#198754" 
        />
        <KPIBlock 
          title="Pending Batches" 
          value={analytics.incompleteBatches} 
          subtitle={`${100 - pctComplete}% Remaining`} 
          icon={<AlertTriangle size={14} />} 
          color="#dc3545" 
        />
      </div>

      {/* BLOCK 2: MATERIAL MASS INVENTORY CONTROL ROW */}
      <div className="row g-2 mb-3">
        <KPIBlock 
          title="Total Mass Managed" 
          value={`${(analytics.totalVolumeKgs || 0).toLocaleString()} KGS`} 
          icon={<Scale size={14} />} 
          color="#6f42c1" 
        />
        <KPIBlock 
          title="Average Batch Weight" 
          value={`${Math.round(analytics.averageBatchSizeKgs || 0).toLocaleString()} KGS`} 
          icon={<Scale size={14} />} 
          color="#fd7e14" 
        />
        <KPIBlock 
          title="Unique Products" 
          value={analytics.productDistribution?.length || 0} 
          icon={<Box size={14} />} 
          color="#20c997" 
        />
      </div>

      {/* BLOCK 3: MULTI-AXIS DISTRIBUTION MATRIX - DESIGN FOOTPRINT ADJUSTED */}
      <div className="row g-2 mb-3">
        <div className="col-xl-4 col-md-6 col-12">
          <TelemetryTrackCard title="Specification Mix Analysis" icon={<Leaf size={12} className="text-success" />}>
            <div className="d-flex align-items-center justify-content-between gap-3 h-100 py-1">
              <MicroDonutChart pct={pctOrganic} color="#198754" label="ORG" />
              <div className="flex-grow-1 vstack gap-2.5">
                <DataHeatRow label="Organic" value={`${(analytics.organicTotalKgs || 0).toLocaleString()} kg`} total={total} pct={pctOrganic} variant="success" countLabel={`${analytics.organicBatches} B`} />
                <DataHeatRow label="Conventional" value={`${(analytics.conventionalTotalKgs || 0).toLocaleString()} kg`} total={total} pct={100 - pctOrganic} variant="secondary" countLabel={`${analytics.conventionalBatches} B`} />
              </div>
            </div>
          </TelemetryTrackCard>
        </div>

        <div className="col-xl-4 col-md-6 col-12">
          <TelemetryTrackCard title="Regulatory Jurisdiction Split" icon={<Globe size={12} className="text-info" />}>
            <div className="d-flex align-items-center justify-content-between gap-3 h-100 py-1">
              <MicroDonutChart pct={pctEu} color="#0dcaf0" label="EU" />
              <div className="flex-grow-1 vstack gap-2.5">
                <DataHeatRow 
                  label="European Union" 
                  value={`${(analytics.euTotalKgs || 0).toLocaleString()} kg`} 
                  total={total} pct={pctEu} variant="info" 
                  countLabel={`${analytics.euBatches} B`}
                  subLabel={`Org: ${(analytics.euOrganicKgs || 0).toLocaleString()} kg | Conv: ${(analytics.euConventionalKgs || 0).toLocaleString()} kg`}
                />
                <DataHeatRow 
                  label="United States" 
                  value={`${(analytics.usaTotalKgs || 0).toLocaleString()} kg`} 
                  total={total} pct={pctUsa} variant="warning" 
                  countLabel={`${analytics.usaBatches} B`}
                  subLabel={`Org: ${(analytics.usaOrganicKgs || 0).toLocaleString()} kg | Conv: ${(analytics.usaConventionalKgs || 0).toLocaleString()} kg`}
                />
              </div>
            </div>
          </TelemetryTrackCard>
        </div>

        <div className="col-xl-4 col-md-12 col-12">
          <TelemetryTrackCard title="Third-Party Risk Assurance" icon={<ShieldCheck size={12} className="text-primary" />}>
            <div className="d-flex align-items-center justify-content-between gap-3 h-100 py-1">
              <MicroDonutChart pct={pctSampling} color="#0d6efd" label="3RD" />
              <div className="flex-grow-1 vstack gap-2.5">
                <DataHeatRow label="Independent Audited" value={analytics.thirdPartySamplingYes} total={total} pct={pctSampling} variant="primary" />
                <DataHeatRow label="Internal Sign-off" value={analytics.thirdPartySamplingNo} total={total} pct={100 - pctSampling} variant="dark" />
              </div>
            </div>
          </TelemetryTrackCard>
        </div>
      </div>

      {/* BLOCK 4: REFACTORED PRODUCT BATCH & VOLUME MATRIX WITH MULTI-AXIS DEEP-DIVE HOVER ENGINE */}
      <div className="row g-2 mb-3">
        <div className="col-12">
          <TelemetryTrackCard 
            title="Product Batch & Weight Allocation Matrix" 
            icon={<Box size={12} className="text-primary" />}
            rightElement={
              <div className="chart-toggle-buttons d-flex rounded bg-light p-0.5">
                <button 
                  className={`btn-toggle-tab ${chartMode === "batches" ? "active-tab" : ""}`}
                  onClick={() => setChartMode("batches")}
                >
                  Total Batches
                </button>
                <button 
                  className={`btn-toggle-tab ${chartMode === "weight" ? "active-tab" : ""}`}
                  onClick={() => setChartMode("weight")}
                >
                  Total Weight
                </button>
              </div>
            }
          >
            <div className="row g-3 mt-1 align-items-stretch flex-column flex-lg-row">
              
              {/* LEFT COLUMN: SCROLLABLE LIST WITH DETAILED MULTI-AXIS INSIGHTS */}
              <div className="col-12 col-lg-5">
                <div className="product-side-matrix-scroller border rounded p-1.5 bg-light bg-opacity-30 vstack gap-1">
                  {analytics.productDistribution && analytics.productDistribution.map((prod, idx) => {
                    const itemPct = total ? Math.round((prod.batchCount / total) * 100) : 0;
                    const referenceCode = `P${(idx + 1).toString().padStart(2, "0")}`;
                    const isTargeted = hoveredIdx === idx;

                    return (
                      <div 
                        key={idx} 
                        className={`matrix-analysis-item p-2 rounded d-flex flex-column gap-1 bg-white ${isTargeted ? "targeted-row" : ""}`}
                        onMouseEnter={() => {
                          setHoveredIdx(idx);
                          const displayLength = Math.min(analytics.productDistribution.length, 12);
                          const spacing = 410 / Math.max(displayLength, 1);
                          const xPos = 60 + (idx * spacing) + 10;
                          const currentVal = chartMode === "batches" ? prod.batchCount : prod.totalWeightKgs;
                          const maxLimit = chartMode === "batches" ? maxBatches : maxWeight;
                          const yPos = 110 - (maxLimit ? (currentVal / maxLimit) * 90 : 0);
                          
                          setTooltipData({ ...prod, x: xPos, y: yPos });
                        }}
                        onMouseLeave={() => {
                          setHoveredIdx(null);
                          setTooltipData(null);
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-2 text-truncate" style={{ maxWidth: "65%" }}>
                            <span className="item-order-badge font-monospace text-primary bg-primary bg-opacity-10 fw-bold">{referenceCode}</span>
                            <span className="fw-bold text-dark text-truncate small-string-label" title={prod.productName}>
                              {prod.productName || "Unknown Variant"}
                            </span>
                          </div>
                          <div className="d-flex align-items-center gap-1.5 text-end flex-shrink-0">
                            <div className="font-monospace text-muted" style={{ fontSize: "0.58rem" }}>
                              <span className="text-dark fw-bold">{prod.batchCount} B</span>
                              <span className="mx-1">•</span>
                              <span>{(prod.totalWeightKgs || 0).toLocaleString()}kg</span>
                            </div>
                            <span className="badge bg-light text-dark border font-monospace fw-bold px-1.5 py-0.5" style={{ fontSize: "0.55rem" }}>
                              {itemPct}%
                            </span>
                          </div>
                        </div>

                        {/* COMPACT PER-PRODUCT MINI SUB-SCALE DATA VISUALIZATION LAYER */}
                        <div className="d-flex gap-2 text-muted font-monospace border-top pt-1 extra-small opacity-80 justify-content-between">
                          <span>EU: <strong>{prod.euBatches}B</strong> / <strong>{Math.round(prod.euWeightKgs).toLocaleString()}kg</strong></span>
                          <span>USA: <strong>{prod.usaBatches}B</strong> / <strong>{Math.round(prod.usaWeightKgs).toLocaleString()}kg</strong></span>
                          <span>Org: <strong>{prod.organicWeightKgs > 0 ? "Yes" : "No"}</strong></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT COLUMN: SCALABLE VECTOR CHART PANEL MAPPED BY CODES WITH REFACTORED TREE-NESTED TOOLTIP */}
              <div className="col-12 col-lg-7 position-relative">
                <div className="interactive-chart-viewport border rounded p-3 bg-white h-100 d-flex flex-column justify-content-center">
                  <svg viewBox="0 0 500 160" className="w-100 h-100">
                    <line x1="45" y1="20" x2="480" y2="20" stroke="#f1f5f9" strokeWidth="1" />
                    <line x1="45" y1="65" x2="480" y2="65" stroke="#f1f5f9" strokeWidth="1" />
                    <line x1="45" y1="110" x2="480" y2="110" stroke="#e2e8f0" strokeWidth="1" />
                    
                    {chartMode === "batches" ? (
                      <>
                        <text x="35" y="23" textAnchor="end" className="svg-axis-labels font-monospace">{maxBatches}</text>
                        <text x="35" y="68" textAnchor="end" className="svg-axis-labels font-monospace">{Math.round(maxBatches / 2)}</text>
                        <text x="35" y="113" textAnchor="end" className="svg-axis-labels font-monospace">0</text>
                      </>
                    ) : (
                      <>
                        <text x="35" y="23" textAnchor="end" className="svg-axis-labels font-monospace">{maxWeight.toLocaleString()}</text>
                        <text x="35" y="68" textAnchor="end" className="svg-axis-labels font-monospace">{Math.round(maxWeight / 2).toLocaleString()}</text>
                        <text x="35" y="113" textAnchor="end" className="svg-axis-labels font-monospace">0</text>
                      </>
                    )}

                    {analytics.productDistribution && analytics.productDistribution.slice(0, 12).map((prod, idx) => {
                      const barWidth = 20;
                      const canvasWidth = 410;
                      const activeLength = Math.min(analytics.productDistribution.slice(0, 12).length, 12);
                      const spacing = canvasWidth / Math.max(activeLength, 1);
                      const xPos = 60 + (idx * spacing);
                      
                      const currentVal = chartMode === "batches" ? prod.batchCount : prod.totalWeightKgs;
                      const maxLimit = chartMode === "batches" ? maxBatches : maxWeight;
                      const barHeight = maxLimit ? (currentVal / maxLimit) * 90 : 0;
                      const yPos = 110 - barHeight;
                      
                      const referenceCode = `P${(idx + 1).toString().padStart(2, "0")}`;
                      const isTargeted = hoveredIdx === idx;

                      return (
                        <g key={idx}
                           className="svg-bar-group-node"
                           onMouseEnter={() => {
                             setHoveredIdx(idx);
                             setTooltipData({ ...prod, x: xPos + (barWidth / 2), y: yPos });
                           }}
                           onMouseLeave={() => {
                             setHoveredIdx(null);
                             setTooltipData(null);
                           }}
                           style={{ opacity: hoveredIdx !== null && !isTargeted ? 0.35 : 1, transition: "opacity 0.15s ease" }}
                        >
                          <rect x={xPos - 4} y="15" width={barWidth + 8} height="100" fill="transparent" style={{ cursor: "pointer" }} />
                          <rect 
                            x={xPos} 
                            y={yPos} 
                            width={barWidth} 
                            height={barHeight} 
                            rx="1.5" 
                            fill={isTargeted ? "#0f172a" : (chartMode === "batches" ? "url(#primaryGradient)" : "url(#purpleGradient)")} 
                            style={{ transition: "all 0.15s ease" }}
                          />
                          <text x={xPos + (barWidth / 2)} y="125" textAnchor="middle" className={`svg-axis-labels ${isTargeted ? "fw-bold text-dark" : ""}`}>
                            {referenceCode}
                          </text>
                        </g>
                      );
                    })}

                    <defs>
                      <linearGradient id="primaryGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0d6efd" stopOpacity="1" />
                        <stop offset="100%" stopColor="#0d6efd" stopOpacity="0.3" />
                      </linearGradient>
                      <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6f42c1" stopOpacity="1" />
                        <stop offset="100%" stopColor="#6f42c1" stopOpacity="0.3" />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* UPDATED: ENTERPRISE TRACEABLE TREE-NESTED FLOATING DATA TOOLTIP */}
                  {tooltipData && (
                    <div 
                      className="svg-live-html-tooltip border shadow-lg p-2.5 rounded-3 bg-white"
                      style={{ 
                        left: `${(tooltipData.x / 500) * 100}%`, 
                        top: `${tooltipData.y - 12}px`
                      }}
                    >
                      <div className="tooltip-title fw-bold text-dark text-truncate mb-2 border-bottom pb-1" style={{ maxWidth: "230px", fontSize: "0.65rem" }}>
                        {tooltipData.productName}
                      </div>
                      
                      <div className="vstack gap-1 font-monospace text-muted" style={{ fontSize: "0.55rem" }}>
                        
                        {/* ROOT LAYER: TOTAL METRICS ANCHOR */}
                        <div className="d-flex justify-content-between gap-4 fw-bold text-dark mb-1">
                          <span>TOTAL METRICS:</span>
                          <span>{tooltipData.batchCount} B | {tooltipData.totalWeightKgs.toLocaleString()} kg</span>
                        </div>
                        
                        {/* SUB-TREE BRANCH 1: EUROPEAN UNION BOUNDS */}
                        <div className="vstack gap-0.5 border-start border-slate-200 ps-2 mb-1.5">
                          <div className="d-flex justify-content-between gap-3 text-primary fw-bold">
                            <span>├── EU REGION:</span>
                            <span>{tooltipData.euBatches} B | {Math.round(tooltipData.euWeightKgs).toLocaleString()} kg</span>
                          </div>
                          <div className="d-flex justify-content-between gap-3 ps-3 opacity-85 text-success">
                            <span>│   ├── ORGANIC:</span>
                            <span>{tooltipData.euOrganicBatches || 0} B | {Math.round(tooltipData.euOrganicWeightKgs || 0).toLocaleString()} kg</span>
                          </div>
                          <div className="d-flex justify-content-between gap-3 ps-3 opacity-85 text-secondary">
                            <span>│   └── CONVENTIONAL:</span>
                            <span>{tooltipData.euConventionalBatches || 0} B | {Math.round(tooltipData.euConventionalWeightKgs || 0).toLocaleString()} kg</span>
                          </div>
                        </div>

                        {/* SUB-TREE BRANCH 2: UNITED STATES BOUNDS */}
                        <div className="vstack gap-0.5 border-start border-slate-200 ps-2">
                          <div className="d-flex justify-content-between gap-3 text-warning fw-bold">
                            <span>└── USA REGION:</span>
                            <span>{tooltipData.usaBatches} B | {Math.round(tooltipData.usaWeightKgs).toLocaleString()} kg</span>
                          </div>
                          <div className="d-flex justify-content-between gap-3 ps-3 opacity-85 text-success">
                            <span>    ├── ORGANIC:</span>
                            <span>{tooltipData.usaOrganicBatches || 0} B | {Math.round(tooltipData.usaOrganicWeightKgs || 0).toLocaleString()} kg</span>
                          </div>
                          <div className="d-flex justify-content-between gap-3 ps-3 opacity-85 text-secondary">
                            <span>    └── CONVENTIONAL:</span>
                            <span>{tooltipData.usaConventionalBatches || 0} B | {Math.round(tooltipData.usaConventionalWeightKgs || 0).toLocaleString()} kg</span>
                          </div>
                        </div>

                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </TelemetryTrackCard>
        </div>
      </div>

      {/* BLOCK 5: ENTERPRISE TELEMETRY METRIC GLOSSARY & LEGEND TRAY */}
      <div className="row g-2">
        <div className="col-12">
          <div className="card glossary-legend-card p-2 border shadow-sm flat bg-light bg-opacity-60">
            <div className="d-flex flex-wrap align-items-center justify-content-start gap-3">
              <div className="d-flex align-items-center text-secondary gap-1 fw-bold tracking-tight text-uppercase header-label" style={{ fontSize: "0.56rem" }}>
                <HelpCircle size={12} className="text-muted" />
                <span>Dashboard Telemetry Glossary & Legend:</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">b, B</span>
                <span className="text-muted">= Batches</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">kg / KGS</span>
                <span className="text-muted">= Kilograms</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">EU</span>
                <span className="text-muted">= European Union</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">USA</span>
                <span className="text-muted">= United States</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">Org</span>
                <span className="text-muted">= ORGANIC</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">Conv</span>
                <span className="text-muted">= CONVENTIONAL</span>
              </div>
              <div className="d-flex align-items-center gap-1.5 glossary-item">
                <span className="glossary-token-key font-monospace">P01, P02...</span>
                <span className="text-muted">= Product Reference Index</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .batch-analytics-engine { width: 100%; background: transparent; }
        .master-compact-density { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 0.735rem !important; }
        .extra-small { font-size: 0.63rem !important; }
        .gap-1.5 { gap: 0.42rem !important; }
        .gap-2.5 { gap: 0.68rem !important; }
        .generic-header-title { font-size: 0.68rem !important; }

        .kpi-block-card { background: #ffffff; border-radius: 4px; border: 1px solid #e2e8f0; transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .kpi-block-card:hover { transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(15, 23, 42, 0.04) !important; border-color: #cbd5e1; }

        .telemetry-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 4px; height: 100%; }
        .telemetry-card-header { border-bottom: 1px solid #f1f5f9; background-color: #f8fafc; padding: 0.47rem 0.68rem !important; }

        .telemetry-track-bg { background-color: #f1f5f9; height: 4px; border-radius: 2px; overflow: hidden; width: 100%; }
        .telemetry-track-fill { height: 100%; border-radius: 2px; transition: width 0.7s ease; }

        .telemetry-donut { width: 55px; height: 55px; border-radius: 50%; display: flex; align-items: center; justify-content: center; position: relative; flex-shrink: 0; }
        .telemetry-donut-center { width: 40px; height: 40px; background: #ffffff; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; font-weight: 700; font-size: 0.58rem; color: #0f172a; line-height: 1; }
        .telemetry-donut-center span { font-size: 0.42rem; color: #64748b; font-weight: 600; margin-top: 2px; }

        .chart-toggle-buttons { border: 1px solid #e2e8f0; }
        .btn-toggle-tab { background: transparent; border: none; font-size: 0.58rem; font-weight: 700; padding: 0.16rem 0.47rem; border-radius: 3px; color: #64748b; text-transform: uppercase; transition: all 0.12s; }
        .btn-toggle-tab.active-tab { background: #ffffff; color: #0f172a; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }

        .interactive-chart-viewport { min-height: 163px; width: 100%; }
        .svg-axis-labels { font-size: 8px; fill: #94a3b8; }
        .svg-bar-group-node rect { transition: fill-opacity 0.15s ease; }
        .svg-bar-group-node:hover rect { fill-opacity: 0.85; }

        .product-side-matrix-scroller { max-height: 200px; overflow-y: auto; background: #fafafa; border-color: #f1f5f9 !important; }
        .product-side-matrix-scroller::-webkit-scrollbar { width: 3px; height: 3px; }
        .product-side-matrix-scroller::-webkit-scrollbar-track { background: transparent; }
        .product-side-matrix-scroller::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        
        .matrix-analysis-item { border: 1px solid #edf2f7; cursor: pointer; transition: all 0.15s ease; }
        .matrix-analysis-item.targeted-row, .matrix-analysis-item:hover { background-color: #f8fafc !important; border-color: #0d6efd !important; transform: translateX(1px); }
        .item-order-badge { font-size: 0.55rem; padding: 0.1rem 0.26rem; border-radius: 3px; letter-spacing: 0.01rem; }
        .small-string-label { font-size: 0.65rem !important; }

        /* Floating Tooltip Custom Styles */
        .svg-live-html-tooltip { position: absolute; z-index: 150; min-width: 255px; pointer-events: none; font-size: 0.58rem; padding: 0.53rem !important; transform: translate(-50%, -105%); border-color: #cbd5e1 !important; box-shadow: 0 12px 28px -4px rgba(15, 23, 42, 0.15) !important; animation: tooltipFade 0.12s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .text-purple { color: #6f42c1 !important; }

        @keyframes tooltipFade {
          from { opacity: 0; transform: translate(-50%, -100%) scale(0.95); }
          to { opacity: 1; transform: translate(-50%, -105%) scale(1); }
        }

        .glossary-legend-card { border-color: #e2e8f0 !important; background-color: #f8fafc !important; }
        .glossary-item { font-size: 0.59rem; color: #475569; font-weight: 500; }
        .glossary-token-key { background: #ffffff; border: 1px solid #cbd5e1; color: #0f172a; padding: 0.05rem 0.26rem; border-radius: 3px; font-weight: 700; font-size: 0.55rem; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
        .header-label { color: #1e293b !important; letter-spacing: 0.02rem; font-size: 0.59rem !important; }

        .btn-refresh-stream { background: #ffffff; border: 1px solid #e2e8f0; padding: 0.26rem; border-radius: 4px; color: #64748b; display: flex; align-items: center; cursor: pointer; }
        .btn-refresh-stream:hover { background: #f1f5f9; color: #0f172a; }
        .animate-pulse { animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .3; } }
      `}</style>
    </div>
  );
}

/* ================= COMPONENT HELPER REUSED UTILS ================= */

const MicroDonutChart = ({ pct, color, label }) => (
  <div className="telemetry-donut" style={{ background: `conic-gradient(${color} 0% ${pct}%, #f1f5f9 ${pct}% 100%)` }}>
    <div className="telemetry-donut-center">
      {pct}%
      <span>{label}</span>
    </div>
  </div>
);

const KPIBlock = ({ title, value, subtitle, icon, color }) => (
  <div className="col-4">
    <div className="card kpi-block-card border-0 shadow-sm h-100" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="card-body p-2 px-2.5 d-flex justify-content-between align-items-center">
        <div>
          <div className="text-muted extra-small fw-bold text-uppercase mb-0 tracking-wider" style={{ fontSize: '0.55rem', color: '#64748b' }}>{title}</div>
          <div className="fw-bold mb-0 text-dark d-flex align-items-baseline gap-1 mt-0.5" style={{ fontSize: "0.89rem", letterSpacing: "-0.01rem" }}>
            {value}
            {subtitle && <span className="text-muted fw-semibold font-monospace" style={{ fontSize: "0.55rem" }}>• {subtitle}</span>}
          </div>
        </div>
        <div className="p-1.5 rounded-1 bg-light d-none d-sm-block" style={{ color: color, opacity: 0.85 }}>
          {icon}
        </div>
      </div>
    </div>
  </div>
);

const TelemetryTrackCard = ({ title, icon, rightElement, children }) => (
  <div className="card telemetry-card border-0 shadow-sm">
    <div className="card-header telemetry-card-header d-flex align-items-center justify-content-between gap-1.5">
      <div className="d-flex align-items-center gap-1.5">
        {icon}
        <span className="fw-bold text-slate-700 tracking-tight text-uppercase" style={{ fontSize: "0.61rem", letterSpacing: "0.01rem" }}>
          {title}
        </span>
      </div>
      {rightElement && rightElement}
    </div>
    <div className="card-body p-2 px-3">
      {children}
    </div>
  </div>
);

const DataHeatRow = ({ label, value, total, pct, variant, countLabel, subLabel }) => (
  <div className="w-100 d-flex flex-column gap-0.5">
    <div className="d-flex justify-content-between align-items-center mb-0 fw-bold text-dark" style={{ fontSize: "0.61rem" }}>
      <span style={{ color: '#334155' }}>{label} {countLabel && <span className="badge bg-light text-secondary border font-monospace ms-1" style={{ fontSize: '0.53rem', padding: '0.1rem 0.2rem' }}>{countLabel}</span>}</span>
      <span className="text-muted font-monospace" style={{ fontSize: "0.58rem" }}>
        {value} <span className="text-dark ms-1 font-weight-bold">{pct}%</span>
      </span>
    </div>
    <div className="telemetry-track-bg">
      <div className={`telemetry-track-fill bg-${variant}`} style={{ width: `${pct}%` }}></div>
    </div>
    {subLabel && <div className="text-muted font-monospace opacity-75 mt-0.5" style={{ fontSize: "0.53rem", lineHeight: "1" }}>{subLabel}</div>}
  </div>
);

const LoaderSkeleton = () => (
  <div className="w-100 text-center py-5 bg-white border border-light rounded-3 shadow-sm">
    <div className="spinner-border spinner-border-sm text-primary mb-2"></div>
    <div className="text-muted extra-small fw-bold text-uppercase tracking-widest">Aggregating Batch Intelligence Metrics...</div>
  </div>
);